import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import LegalNotice from "@/components/LegalNotice";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    display_name: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptTerms) {
      toast.error("Please read and accept the Hunt57 Terms to continue.");
      return;
    }
    setLoading(true);
    const res = await register({
      ...form,
      email: form.email.trim().toLowerCase(),
      username: form.username.trim().toLowerCase(),
      accept_terms: true,
    });
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome to Hunt57.");
      navigate("/browse");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen text-white font-body flex flex-col">
      <div className="p-4 md:p-8">
        <Link to="/" className="flex items-center" data-testid="register-back-home">
          <Logo variant="nav" alt="Hunt57" />
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          <div className="font-mono text-xs text-[#FF5F1F] tracking-widest uppercase">→ Sign Up</div>
          <h1 className="mt-3 font-display font-black uppercase text-4xl tracking-tighter">Join the Hunt</h1>
          <p className="mt-3 text-zinc-400">
            Create a free account to browse cards, message sellers, and build your watchlist.
            Ready to sell? You can upgrade to a vendor account with a referral code anytime.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="h57-label">Username</label>
              <input
                required
                minLength={3}
                maxLength={24}
                pattern="[a-zA-Z0-9_\-]+"
                title="3-24 characters. Letters, numbers, underscore, hyphen only. No @ or dots."
                data-testid="register-username"
                className="h57-input"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="cardhunter"
              />
              <div className="mt-1 font-mono text-[10px] text-zinc-500 tracking-widest uppercase">Letters, numbers, _ or - only</div>
            </div>
            <div>
              <label className="h57-label">Display Name</label>
              <input
                data-testid="register-display-name"
                className="h57-input"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder="Card Hunter"
              />
            </div>
            <div>
              <label className="h57-label">Email</label>
              <input
                type="email"
                required
                data-testid="register-email"
                className="h57-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label className="h57-label">Password</label>
              <input
                type="password"
                required
                minLength={6}
                data-testid="register-password"
                className="h57-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            <LegalNotice
              variant="account"
              checked={acceptTerms}
              onChange={setAcceptTerms}
              testId="register-legal"
            />
            <button type="submit" disabled={loading || !acceptTerms} className="h57-btn-primary w-full" data-testid="register-submit">
              {loading ? "Creating…" : "Create Free Account"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-zinc-500">
            Have an account?{" "}
            <Link to="/login" className="text-[#FF5F1F] hover:underline font-bold" data-testid="register-link-signin">
              Sign in →
            </Link>
          </p>
          <p className="mt-2 text-center text-xs text-zinc-600">
            Want to list cards? Upgrade to a vendor account after signing up.
          </p>
        </div>
      </div>
    </div>
  );
}
